import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  MonitorPlay, Radio, Layers, MessageSquareText, Clapperboard, PackageCheck,
  HeartHandshake, Smartphone, Megaphone, BarChart3,
} from 'lucide-react';
import api from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// key 는 내부 식별자(카드 이미지 키)로만 사용 · 화면에는 번호를 표시하지 않는다.
// detailPath: 기능 상세 페이지가 준비되면 경로만 채운다 (null = 준비 중).
export const FEATURES = [
  { key: '01', title: 'LIVE 통합 콘솔', desc: '방송·상품·댓글을 한 화면에서 관리', icon: MonitorPlay, group: 'live', detailPath: '/features/live-console' },
  { key: '02', title: '3사 동시송출', desc: '여러 외부 플랫폼으로 동시 방송', icon: Radio, group: 'live', detailPath: null },
  { key: '03', title: '상품정보 오버레이', desc: '상품 이미지·가격·옵션 등을 방송 화면에 표시', icon: Layers, group: 'live', detailPath: null },
  { key: '04', title: '댓글 주문 처리', desc: '방송 댓글을 주문 후보로 연결', icon: MessageSquareText, group: 'ops', detailPath: null },
  { key: '05', title: '방송 종료 후 계속 판매', desc: '녹화영상과 상품페이지를 연결하여 판매', icon: Clapperboard, group: 'ops', detailPath: null },
  { key: '06', title: '주문·결제·출고 통합', desc: '주문·결제·입금확인·바코드 출고 관리', icon: PackageCheck, group: 'ops', detailPath: null },
  { key: '07', title: '단골·팔로우 / VVIP·블랙리스트', desc: '단골·팔로우 고객과 VVIP·블랙리스트 관리', icon: HeartHandshake, group: 'ops', detailPath: null },
  { key: '08', title: '숏폼 게시·통합댓글관리', desc: '숏폼 게시와 댓글을 한 곳에서 관리', icon: Smartphone, group: 'ops', detailPath: null },
  { key: '09', title: 'LIVE·상품 광고', desc: 'LIVE 방송과 상품 광고 운영', icon: Megaphone, group: 'ops', detailPath: null },
  { key: '10', title: '방송·매출·고객 통계', desc: '방송·매출·고객 통계 확인', icon: BarChart3, group: 'ops', detailPath: null },
];

export const FEATURE_GROUPS = [
  { key: 'live', label: 'LIVE 방송기능' },
  { key: 'ops', label: '운영기능' },
];

// 상단 메뉴·하단 카드 공통 이동 규칙: 상세 페이지 → 이동 / 없으면 「준비 중」 안내
export const useOpenFeature = () => {
  const navigate = useNavigate();
  return (f) => (f.detailPath ? navigate(f.detailPath) : toast.info(`${f.title} 상세 안내는 준비 중입니다.`));
};

export const DEFAULT_CONTENT = {
  hero_mode: 'image',
  hero_image_pc: '/main-renewal/hero-pc.jpg',
  hero_image_mobile: '/main-renewal/hero-mobile.jpg',
  hero_video_pc: null,
  hero_video_mobile: null,
  card_images: {},
  intro_mode: 'text',
  intro_image: null,
  self_live_banner_visible: true,
  self_live_banner_title: '외부 LIVE는 고객을 모으고, 자사 LIVE는 단골을 만듭니다.',
  self_live_banner_desc: 'TikTok·Instagram·YouTube에서 새로운 고객을 만나고,\nLIVEMOA 자사 LIVE에서 우리 브랜드의 단골과 재구매 고객을 만들어 보세요.',
};

export const mediaUrl = (u) => (u && u.startsWith('/api/') ? `${BACKEND_URL}${u}` : u || null);

export const heroVideoFor = (c, isPc) => {
  if (!c || c.hero_mode !== 'video') return null;
  return mediaUrl(isPc ? (c.hero_video_pc || c.hero_video_mobile) : (c.hero_video_mobile || c.hero_video_pc));
};

export const sellerCenterPath = (user) => (user?.seller_status === 'approved' ? '/seller/dashboard' : '/seller/apply');

export const useMainContent = () => {
  const [content, setContent] = useState(null);
  useEffect(() => {
    api.get('/public/main-page')
      .then((r) => setContent(r.data))
      .catch(() => setContent(DEFAULT_CONTENT));
  }, []);
  return content;
};
